import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Email as EmailIcon,
  Link as LinkIcon,
  LinkOff as LinkOffIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Sync as SyncIcon,
} from '@mui/icons-material';
import { useGmail } from '../../hooks/useGmail';
import { GmailConnectionStatus } from '../../types/gmail';

export function GmailSettings() {
  const { 
    loading, 
    error, 
    checkConnection, 
    connectGmail, 
    completeConnection, 
    disconnectGmail,
    clearError 
  } = useGmail();
  
  const [connectionStatus, setConnectionStatus] = useState<GmailConnectionStatus>({ isConnected: false });
  const [isConnecting, setIsConnecting] = useState(false);

  // Check connection status on component mount
  useEffect(() => {
    const checkStatus = async () => {
      const status = await checkConnection();
      setConnectionStatus(status);
    };
    checkStatus();
  }, [checkConnection]);

  // Check for success redirect from OAuth callback
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('gmail_connected') === 'true') {
      // Refresh connection status after successful OAuth
      checkConnection().then(setConnectionStatus);
      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [checkConnection]);

  // Handle OAuth callback if we're returning from Google
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (error) {
      clearError();
      setConnectionStatus({ isConnected: false });
      return;
    }

    if (code && state && isConnecting) {
      const handleCallback = async () => {
        const success = await completeConnection(code, state);
        if (success) {
          const status = await checkConnection();
          setConnectionStatus(status);
        }
        setIsConnecting(false);
        
        // Clean up URL
        window.history.replaceState({}, document.title, window.location.pathname);
      };
      handleCallback();
    }
  }, [completeConnection, checkConnection, clearError, isConnecting]);

  const handleConnect = async () => {
    setIsConnecting(true);
    clearError();
    
    const result = await connectGmail();
    if (result?.authUrl) {
      // Redirect to Google OAuth
      window.location.href = result.authUrl;
    } else {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    const success = await disconnectGmail();
    if (success) {
      setConnectionStatus({ isConnected: false });
    }
  };

  const handleRefreshStatus = async () => {
    const status = await checkConnection();
    setConnectionStatus(status);
  };

  return (
    <Box sx={{ mb: 4, pb: 3, borderBottom: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <EmailIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>
          Gmail Integration
        </Typography>
        {connectionStatus.isConnected && (
          <Chip
            icon={<CheckCircleIcon />}
            label="Connected"
            color="success"
            size="small"
          />
        )}
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Connect your Gmail account to view emails, add contacts from emails, and reply to emails directly from PrayTell.
      </Typography>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          onClose={clearError}
        >
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 2 }}>
        {connectionStatus.isConnected ? (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CheckCircleIcon color="success" />
              <Typography variant="h6">
                Gmail Connected
              </Typography>
            </Box>

            <List dense>
              <ListItem>
                <ListItemIcon>
                  <EmailIcon />
                </ListItemIcon>
                <ListItemText
                  primary="Connected Account"
                  secondary={connectionStatus.emailAddress}
                />
              </ListItem>
              
              {connectionStatus.tokenExpiry && (
                <ListItem>
                  <ListItemIcon>
                    <SyncIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary="Token Expires"
                    secondary={new Date(connectionStatus.tokenExpiry).toLocaleString()}
                  />
                </ListItem>
              )}
            </List>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<SyncIcon />}
                onClick={handleRefreshStatus}
                disabled={loading}
              >
                Refresh Status
              </Button>
              
              <Button
                variant="outlined"
                color="error"
                startIcon={<LinkOffIcon />}
                onClick={handleDisconnect}
                disabled={loading}
              >
                Disconnect
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <ErrorIcon color="warning" />
              <Typography variant="h6">
                Gmail Not Connected
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Connect your Gmail account to enable email integration features.
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={isConnecting ? <CircularProgress size={20} /> : <LinkIcon />}
                onClick={handleConnect}
                disabled={loading || isConnecting}
              >
                {isConnecting ? 'Connecting...' : 'Connect Gmail'}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {connectionStatus.isConnected && (
        <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
          <Typography variant="subtitle2" gutterBottom>
            What you can do with Gmail integration:
          </Typography>
          <List dense>
            <ListItem sx={{ py: 0 }}>
              <ListItemText
                primary="• View your Gmail inbox"
                secondary="Browse and read emails directly in PrayTell"
              />
            </ListItem>
            <ListItem sx={{ py: 0 }}>
              <ListItemText
                primary="• Add contacts from emails"
                secondary="Create contacts from email senders"
              />
            </ListItem>
            <ListItem sx={{ py: 0 }}>
              <ListItemText
                primary="• Send and reply to emails"
                secondary="Compose emails and reply to conversations"
              />
            </ListItem>
            <ListItem sx={{ py: 0 }}>
              <ListItemText
                primary="• Sync emails to events"
                secondary="Track email interactions as events"
              />
            </ListItem>
          </List>
        </Paper>
      )}
    </Box>
  );
}
